/**
 * Ticket Number Service
 *
 * Creates a service request or fault with its human-readable ticket number
 * ("SR-0042" / "FLT-0017") in ONE transaction: read the tenant's counter
 * (tenants/{tenantId}/counters/ticket_number), bump this kind's sequence,
 * and write the new ticket doc. Two people creating at once get different
 * numbers, and a failed create neither burns a number nor leaves a ticket
 * without one. Modelled on account-number.service.ts.
 *
 * TWIN: functions/src/mcp/faults.ts raiseFault reserves from the same
 * counter doc and field with the Admin SDK. Format + path live in
 * shared/src/domain/ticket-number.ts.
 */

import { collection, doc, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';
import { stripUndefinedDeep } from '@/utils/firestore';
import {
  currentTicketSeq,
  formatTicketNumber,
  ticketCounterPath,
  type TicketKind,
} from '@shared/domain/ticket-number';

const COLLECTION_FOR_KIND: Record<TicketKind, string> = {
  service_request: 'service_requests',
  fault: 'faults',
};

export interface CreatedTicket {
  id: string;
  ticketNumber: string;
}

/**
 * Create a ticket doc of `kind` for `tenantId` with the next ticket number.
 * The doc is written with that same `tenantId`, so the counter and the
 * ticket can never belong to different tenants.
 */
export async function createTicketWithNumber<T extends object>(
  kind: TicketKind,
  tenantId: string,
  data: T,
): Promise<CreatedTicket> {
  const counterRef = doc(db, ...ticketCounterPath(tenantId));
  const ticketRef = doc(collection(db, COLLECTION_FOR_KIND[kind]));

  const ticketNumber = await runTransaction(db, async (tx) => {
    const snap = await tx.get(counterRef);
    const seq = currentTicketSeq(snap.exists() ? snap.data() : undefined, kind) + 1;
    const number = formatTicketNumber(kind, seq);

    tx.set(counterRef, { [kind]: seq, updatedAt: new Date().toISOString() }, { merge: true });
    tx.set(ticketRef, stripUndefinedDeep({ ...data, tenantId, ticketNumber: number }));
    return number;
  });

  return { id: ticketRef.id, ticketNumber };
}
